import { useState, useEffect, useRef } from 'react';
import type { ProductDesignerConfig } from '@customedge/shared';
import { safeGetItem, safeSetItem, safeRemoveItem } from '../lib/safeStorage';
import { getRequestedColorVariantNameFromSearch } from './useProductSetup';

const API_BASE = '/api';

// Pure, DOM-free so this is unit-testable without mocking `window` — exported
// for that reason, no behavior change versus the inline logic this replaces.
export function isPopupModeFromSearch(search: string): boolean {
  return new URLSearchParams(search).get('mode') === 'popup';
}

// `mode` must survive this cleanup alongside `sku` — `notifyParent()` below
// re-derives popup-mode from the *current* URL on every call (not from this
// hook's own already-correct `isPopupMode` state), so dropping `mode` here
// would silently break every DESIGNER_SAVED/DESIGNER_CLOSED postMessage for
// the rest of the session, the instant this effect runs on mount — before any
// real user interaction ever has a chance to trigger one. (Real bug, found
// while building a standalone demo storefront that embeds this app in an
// iframe with `?mode=popup` — the documented integration contract.)
export function buildCleanedSearch(search: string): string {
  const params = new URLSearchParams(search);
  const keep = new URLSearchParams();
  const sku = params.get('sku');
  const mode = params.get('mode');
  const admin = params.get('admin');
  // `design` (DesignerHeader's Share feature — see useSharedDesignLoader.ts)
  // must survive this cleanup too: it runs on the very same mount as this
  // effect, and this replaceState would otherwise erase it from the URL
  // before that hook's own effect gets a chance to read it, silently
  // degrading every shared-design link back to a blank design.
  const design = params.get('design');
  // `logo` (merchant branding — see DesignerHeader's brand mark, top-left of
  // the header before the Design/Preview/3D tabs) — Magento's design-launcher.js
  // appends it to the iframe src from the admin-configured
  // customedge_connector/general/logo value. Must survive this cleanup the
  // same as `sku`/`mode`: read once below, then stripped from the visible URL.
  const logo = params.get('logo');
  // `logoLink` — where clicking the brand mark navigates to (e.g. the store
  // homepage). Independent of `logo` itself; must survive the same cleanup.
  const logoLink = params.get('logoLink');
  // `sides` — comma-separated, lowercase side labels to restrict which sides
  // load (see useProductSetup.ts). Must survive this cleanup the same as
  // `sku`: a page refresh with no code re-reading the original launch URL
  // would otherwise silently fall back to loading every side.
  const sides = params.get('sides');
  // `printColors` — comma-separated allowed print-color choices (e.g.
  // `full-color,1,2`), narrowing the admin-configured ProductConfig.printColorOptions
  // set for this launch — see useProductSetup.ts's
  // getRequestedPrintColorOptionsFromSearch. Must survive this cleanup the
  // same as `sides`: without this, a page refresh silently drops back to the
  // full admin-configured set instead of the requested subset.
  const printColors = params.get('printColors');
  // `colorVariant`/`printMethod`/`printColor` — pre-select a single Product
  // color variant / print method / print color at launch (distinct from
  // `printColors` above, which narrows the whole admin-configured SET rather
  // than picking one) — see useProductSetup.ts's
  // getRequestedColorVariantNameFromSearch/getRequestedPrintMethodFromSearch/
  // getRequestedPrintColorFromSearch. Also the same params DesignerHeader's
  // pill selectors write back via history.replaceState once the customer
  // actually picks something in ProductOptionsModal, so this three must
  // survive a page refresh — see ProductOptionsModal.tsx's handleApply.
  const colorVariant = params.get('colorVariant');
  const printMethod = params.get('printMethod');
  const printColor = params.get('printColor');
  // `printPositions`/`printMethodByPosition`/`printColorByPosition` — the
  // multiselect Print Position feature's own launch params (see
  // useProductSetup.ts's getRequestedPrintPositionsFromSearch/
  // getRequestedPrintMethodByPositionFromSearch/
  // getRequestedPrintColorByPositionFromSearch). Must survive this cleanup
  // the same as colorVariant/printMethod/printColor above — also written
  // back via history.replaceState by ProductOptionsModal's own
  // syncSelectionsToUrl, so must round-trip through a page refresh too.
  // `printColorByPosition` was added after the other two and initially
  // missed here — confirmed live 2026-08-04 via a direct console trace that
  // window.location.search never even contained the param by the time
  // useProductSetup's effect ran, despite Magento/React correctly sending it
  // and the parser itself working fine in isolation; this allowlist gap was
  // the actual root cause, not a parsing bug.
  const printPositions = params.get('printPositions');
  const printMethodByPosition = params.get('printMethodByPosition');
  const printColorByPosition = params.get('printColorByPosition');
  // `hash` — the "My Design" retention key Magento's design-launcher.js
  // appends (`promofit_{customerId}` for a logged-in customer, stable forever;
  // `promofit_guest_{magentoPhpSessionId}` for a guest, valid only for that
  // one Magento session by design). Must survive the same as `sku`/`mode`: a
  // page refresh with no code re-reading the original launch URL would
  // otherwise silently drop back to the "sign in to see My Designs" state for
  // a guest who still has a live Magento session.
  const hash = params.get('hash');
  // `dwip` — "Design WIP" continuous-editing id (see useDesignWip.ts). Must
  // survive this cleanup the same as `design`: a resume link
  // (`?sku=...&dwip={uuid}`) opened fresh needs this param intact for
  // useDesignWip's own effect to read once the canvas mounts, moments after
  // this replaceState runs.
  const dwip = params.get('dwip');
  // Magento cart-linking identity (see useDesignerInit's own effect below and
  // magentoCartContext.ts) — quoteId/guestkey/cartUrl/quoteUrl/returnPath must
  // all survive this cleanup exactly like `sku`: they're only consumed later,
  // at Add-to-Cart time (CartPopup.tsx), well after this mount-time effect has
  // already stripped/replaced the URL once.
  // `magentoToken` and `storeId` deliberately are NOT kept here — like
  // `mtoken`/`config`, they're read once below and persisted to
  // sessionStorage instead (same pattern as `hash`), so a raw customer bearer
  // token doesn't sit in the visible URL/browser history for the rest of the
  // session, and `storeId` behavior stays consistent with the existing
  // mtoken/config cleanup this function already did before this change.
  const quoteId = params.get('quoteId');
  const guestkey = params.get('guestkey');
  const cartUrl = params.get('cartUrl');
  const quoteUrl = params.get('quoteUrl');
  const returnPath = params.get('return_path');
  const customOptions = params.get('customOptions');
  const superAttribute = params.get('superAttribute');
  if (sku) keep.set('sku', sku);
  if (mode) keep.set('mode', mode);
  if (admin) keep.set('admin', admin);
  if (design) keep.set('design', design);
  if (dwip) keep.set('dwip', dwip);
  if (logo) keep.set('logo', logo);
  if (logoLink) keep.set('logoLink', logoLink);
  if (sides) keep.set('sides', sides);
  if (printColors) keep.set('printColors', printColors);
  if (colorVariant) keep.set('colorVariant', colorVariant);
  if (printMethod) keep.set('printMethod', printMethod);
  if (printColor) keep.set('printColor', printColor);
  if (printPositions) keep.set('printPositions', printPositions);
  if (printMethodByPosition) keep.set('printMethodByPosition', printMethodByPosition);
  if (printColorByPosition) keep.set('printColorByPosition', printColorByPosition);
  if (hash) keep.set('hash', hash);
  if (quoteId) keep.set('quoteId', quoteId);
  if (guestkey) keep.set('guestkey', guestkey);
  if (cartUrl) keep.set('cartUrl', cartUrl);
  if (quoteUrl) keep.set('quoteUrl', quoteUrl);
  if (returnPath) keep.set('return_path', returnPath);
  if (customOptions) keep.set('customOptions', customOptions);
  if (superAttribute) keep.set('superAttribute', superAttribute);
  // `variant_{label}=option` (see useProductSetup.ts's
  // getRequestedVariantSelectionsFromSearch) — one dynamically-named param per
  // variant group, so unlike every fixed key above this needs a prefix scan
  // rather than a single `.get()`. Must survive the same as `sides`: a page
  // refresh would otherwise silently drop back to auto-selected defaults.
  for (const [key, value] of params.entries()) {
    if (key.startsWith('variant_')) keep.set(key, value);
  }
  return keep.toString() ? `?${keep}` : '';
}

export function useDesignerInit() {
  const [config,        setConfig]        = useState<ProductDesignerConfig | null>(null);
  const [token,         setToken]         = useState<string | null>(null);
  const [error,         setError]         = useState<string | null>(null);
  const [isPopupMode,   setIsPopupMode]   = useState(false);
  const [isAdminMode,   setIsAdminMode]   = useState(false);
  const [brandLogoUrl,  setBrandLogoUrl]  = useState<string | null>(null);
  const [brandLogoLink, setBrandLogoLink] = useState<string | null>(null);
  const initialized = useRef(false);

  useEffect(() => {
    // Guard against React 18 StrictMode double-invocation: this effect reads
    // URL params and then strips them via replaceState, so a second run would
    // see an already-cleaned URL and incorrectly report a missing SKU.
    if (initialized.current) return;
    initialized.current = true;

    const params = new URLSearchParams(window.location.search);

    // Detect popup mode
    setIsPopupMode(isPopupModeFromSearch(window.location.search));

    // Admin mode: the admin Templates page links here with `?admin=1` so a
    // staff member can build a layout on the real canvas instead of hand-
    // typing base64 Fabric JSON. Reveals the "Save as Template" action only —
    // the backend's own `authenticateAdmin` middleware is what actually
    // enforces the role, this is just the UI gate.
    setIsAdminMode(params.get('admin') === '1');

    // Merchant branding logo, passed by Magento's design-launcher.js from the
    // admin-configured customedge_connector/general/logo value. Read as a
    // plain string (not validated as a URL) — DesignerHeader just renders it
    // in an <img src>, and an invalid value simply fails to load the image.
    const logo = params.get('logo');
    if (logo) setBrandLogoUrl(logo);

    // Where clicking the brand mark navigates to (e.g. the store homepage) —
    // same admin-configured/URL-param pattern as `logo` itself, just a
    // separate value so the image source and the click target aren't
    // conflated into one field.
    const logoLink = params.get('logoLink');
    if (logoLink) setBrandLogoLink(logoLink);

    // "My Design" retention key, passed by Magento's design-launcher.js (see
    // buildCleanedSearch's own comment on `hash` for the two forms it takes).
    // Persisted to sessionStorage — same pattern as `customedge_token` below —
    // so MyDesignsTool.tsx (mounted later, in the sidebar) can still read it
    // after this effect strips it from the visible URL.
    const hash = params.get('hash');
    if (hash) safeSetItem('session', 'customedge_design_hash', hash);

    // Magento cart-linking identity/context — cleared unconditionally on every
    // mount BEFORE conditionally re-setting from the current URL. Without this,
    // a customer who opens the designer twice in the same browser tab (e.g.
    // once from a real Magento redirect with guestkey/quoteId present, then
    // later from a bookmark/shared-design link/direct `?sku=...` with no
    // Magento params at all) would silently inherit the FIRST session's
    // leftover guestkey/quoteId/cartUrl — sessionStorage persists across
    // mounts within a tab and nothing else in this codebase ever clears it.
    // That stale context would make Add to Cart (magentoCartContext.ts) add
    // the product to a stranger cart and redirect to a stale cart URL, both
    // silently. Found during a post-implementation review, not live yet.
    const MAGENTO_CONTEXT_KEYS = [
      'customedge_magento_token',
      'customedge_magento_quote_id',
      'customedge_magento_guestkey',
      'customedge_magento_cart_url',
      'customedge_magento_quote_url',
      'customedge_magento_return_path',
      'customedge_magento_custom_options',
      'customedge_magento_super_attribute',
      // customedge_magento_store_id is intentionally NOT cleared here — it has
      // its own unconditional (always-set, default '0') write further below,
      // same as it's always read for the mtoken exchange regardless of
      // whether this is a Magento-cart-linked launch at all.
    ];
    for (const key of MAGENTO_CONTEXT_KEYS) safeRemoveItem('session', key);

    // Raw Magento customer bearer token, distinct from `mtoken` below (which
    // is exchanged once, server-side, for this app's OWN JWT and then
    // discarded) and from `token` (the admin-panel JWT passthrough — see
    // `explicitToken` further down). This one is needed later, verbatim, to
    // call Magento's own `carts/mine/items` REST API directly when the
    // customer clicks Add to Cart (see magentoCartContext.ts) — so it's
    // persisted to sessionStorage the same way `customedge_design_hash` is,
    // read once here before the URL is cleaned up below.
    const magentoToken = params.get('magentoToken');
    if (magentoToken) safeSetItem('session', 'customedge_magento_token', magentoToken);

    // Cart-linking identity/context, all optional and all read once here then
    // persisted the same way as `magentoToken` above — CartPopup.tsx's Add to
    // Cart handler needs them well after this mount-time effect has already
    // stripped them from the visible URL (see buildCleanedSearch above for why
    // they're kept in the URL through the replaceState too, in case of an
    // in-page reload before Add to Cart is clicked).
    const quoteId = params.get('quoteId');
    if (quoteId) safeSetItem('session', 'customedge_magento_quote_id', quoteId);
    const guestkey = params.get('guestkey');
    if (guestkey) safeSetItem('session', 'customedge_magento_guestkey', guestkey);
    const cartUrl = params.get('cartUrl');
    if (cartUrl) safeSetItem('session', 'customedge_magento_cart_url', cartUrl);
    const quoteUrl = params.get('quoteUrl');
    if (quoteUrl) safeSetItem('session', 'customedge_magento_quote_url', quoteUrl);
    const returnPath = params.get('return_path');
    if (returnPath) safeSetItem('session', 'customedge_magento_return_path', returnPath);

    // The customer's already-made PDP selections (Magento super_attribute[id]/
    // options[id] pairs — e.g. color swatch, Print Method/Print Color custom
    // options), forwarded verbatim by RedirectUrl.php's designUrl branch as
    // JSON so this designer app can send them back unchanged on Add to Cart
    // (magentoCartContext.ts) without needing its own label->id mapping. This
    // app never resolves what these mean — it just carries them through.
    const customOptions = params.get('customOptions');
    if (customOptions) safeSetItem('session', 'customedge_magento_custom_options', customOptions);
    const superAttribute = params.get('superAttribute');
    if (superAttribute) safeSetItem('session', 'customedge_magento_super_attribute', superAttribute);

    // Read product SKU / config from URL
    const sku = params.get('sku');
    const configParam = params.get('config');

    if (sku) {
      // Fetch product config from backend by SKU. `?colorVariant=` (when
      // present) tells the backend which color variant to resolve as
      // "active" and send full imageSides/areas for up front — see
      // products.ts's own comment; every other variant comes back with
      // areasLoaded: false and gets its real areas fetched on demand
      // (designStore.ts's setActiveColorVariant callers) only if/when the
      // customer actually switches to it. A `?dwip=`/saved-design
      // colorVariantId can still override this after the fact — same
      // fallback-fetch path handles that too, unchanged from before this
      // param existed.
      const colorVariantName = getRequestedColorVariantNameFromSearch(window.location.search);
      const configUrl = colorVariantName
        ? `${API_BASE}/products/${sku}/config?colorVariant=${encodeURIComponent(colorVariantName)}`
        : `${API_BASE}/products/${sku}/config`;
      fetch(configUrl)
        .then(r => r.ok ? r.json() : Promise.reject('Product not found'))
        .then(data => setConfig(data))
        .catch(err => setError(typeof err === 'string' ? err : 'Product config not found'));
    } else if (configParam) {
      // Decode inline config from URL parameter
      try {
        const decoded = JSON.parse(atob(configParam)) as ProductDesignerConfig;
        setConfig(decoded);
      } catch {
        setError('Invalid product configuration');
        return;
      }
    } else {
      setError('Missing product SKU or configuration');
      return;
    }

    // Exchange Magento token if present
    const mtoken = params.get('mtoken');
    const storeId = params.get('storeId') ?? '0';
    // Persisted (not kept in the visible URL — see buildCleanedSearch, which
    // deliberately drops storeId same as mtoken/config) so magentoCartContext.ts
    // can still read it later, at Add to Cart time, after this cleanup runs.
    safeSetItem('session', 'customedge_magento_store_id', storeId);
    // Admin panel hand-off: it already holds a valid admin JWT (same
    // JWT_SECRET/auth as this app) and passes it straight through — no
    // separate admin login flow exists in this app, so this is the only
    // path that lets `/api/admin/templates` calls below succeed.
    const explicitToken = params.get('token');

    if (explicitToken) {
      setToken(explicitToken);
      safeSetItem('session', 'customedge_token', explicitToken);
    } else if (mtoken) {
      fetch(`${API_BASE}/auth/magento-exchange`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ magentoToken: mtoken, storeId }),
      })
        .then(r => r.json())
        .then(data => {
          if (data.accessToken) {
            setToken(data.accessToken);
            safeSetItem('session', 'customedge_token', data.accessToken);
          }
        })
        .catch(() => {
          // Non-fatal: continue as guest
          const existingToken = safeGetItem('session', 'customedge_token');
          if (existingToken) setToken(existingToken);
        });
    } else {
      const existingToken = safeGetItem('session', 'customedge_token');
      if (existingToken) setToken(existingToken);
    }

    // Strip sensitive params (mtoken, config) but keep sku + mode — see
    // `buildCleanedSearch()`'s own comment for why `mode` matters here.
    const newSearch = buildCleanedSearch(window.location.search);
    window.history.replaceState({}, '', window.location.pathname + newSearch);
  }, []);

  return { config, token, error, isPopupMode, isAdminMode, brandLogoUrl, brandLogoLink };
}

export function notifyParent(type: string, payload?: any) {
  const isPopup = isPopupModeFromSearch(window.location.search);
  if (isPopup && window.parent !== window) {
    const origin = import.meta.env.VITE_ALLOWED_PARENT_ORIGIN ?? '*';
    window.parent.postMessage({ type, payload }, origin);
  }
}
